import { useIntl } from 'react-intl'
import { RESOURCE_MESSAGES as R_ } from '../resourceCenterMessages'

const WIDTH_ICON = 35
const THUMB_SIZE = 40
const GRID_ICON_SIZE = 80

// Helper function to extract extension
const getFileExtension = (file: string): string => {
  const cleaned = (file || '').toLowerCase()
  const noQuery = cleaned.split('?')[0].split('#')[0]
  const filename = noQuery.split('/').pop() || ''
  return filename.includes('.') ? (filename.split('.').pop() as string) : ''
}

// Helper function to check if file is an image
const isImageFile = (extension: string): boolean => {
  const fileImageArr = [
    'jpg',
    'jpeg',
    'jpe',
    'jfif',
    'png',
    'gif',
    'bmp',
    'tif',
    'tiff',
    'svg',
    'webp',
    'ico',
    'heic',
    'heif',
  ]
  return fileImageArr.includes(extension)
}

// Component for grid view (larger icons and images)
export const GridFilePreview = ({file}: {file: string}) => {
  const intl = useIntl()
  const extension = getFileExtension(file)

  // If we couldn't detect an extension, show a generic icon
  if (!extension) {
    return (
      <div className="text-center">
        <img src='/media/tfi/files/txt.png' alt={intl.formatMessage(R_.fileIconAlt)} width={GRID_ICON_SIZE} />
        <div className="mt-2 fw-bold">{intl.formatMessage(R_.fileDocument)}</div>
      </div>
    )
  }

  // Word
  const fileWordArr = ['doc', 'docx', 'docm', 'dot', 'dotx', 'dotm', 'rtf']
  // Excel
  const fileExcelArr = ['xls', 'xlsx', 'xlsm', 'xlsb', 'xlt', 'xltx', 'xltm', 'csv']
  // PowerPoint
  const filePptArr = ['ppt', 'pptx', 'pptm', 'pot', 'potx', 'potm', 'pps', 'ppsx', 'ppsm']
  // Videos
  const fileVideoArr = ['mp4', 'mov', 'avi', 'wmv', 'flv', 'mkv', 'webm', 'mpeg', '3gp', 'ogg']
  // PDF
  const filePdfArr = ['pdf']
  // Audio
  const fileAudioArr = ['mp3', 'wav', 'aac', 'flac', 'ogg', 'wma', 'm4a', 'aiff', 'alac']
  // Text-ish
  const fileTextArr = ['txt', 'md', 'log', 'json', 'xml']
  // Compressed
  const fileCompressedArr = ['zip', 'rar', '7z', 'tar', 'gz']

  // For images, show full preview
  if (isImageFile(extension)) {
    return (
      <img
        src={file}
        alt={extension}
        style={{width: '100%', height: '100%', objectFit: 'cover'}}
        onError={(e) => {
          // Fallback to generic icon if image cannot be loaded
          const target = e.currentTarget as HTMLImageElement
          target.onerror = null
          target.src = '/media/tfi/files/txt.png'
          target.style.width = `${GRID_ICON_SIZE}px`
          target.style.height = 'auto'
          target.style.objectFit = 'contain'
        }}
      />
    )
  }

  // For other file types, show large icon with label
  if (fileWordArr.includes(extension)) {
    return (
      <div className="text-center">
        <img src='/media/tfi/files/word.webp' alt={extension} width={GRID_ICON_SIZE} />
        <div className="mt-2 fw-bold">{intl.formatMessage(R_.fileWord)}</div>
      </div>
    )
  }

  if (fileExcelArr.includes(extension)) {
    return (
      <div className="text-center">
        <img src='/media/tfi/files/excel.webp' alt={extension} width={GRID_ICON_SIZE} />
        <div className="mt-2 fw-bold">{intl.formatMessage(R_.fileExcel)}</div>
      </div>
    )
  }

  if (filePptArr.includes(extension)) {
    return (
      <div className="text-center">
        <img src='/media/tfi/files/powerpoint.webp' alt={extension} width={GRID_ICON_SIZE} />
        <div className="mt-2 fw-bold">{intl.formatMessage(R_.filePowerPoint)}</div>
      </div>
    )
  }

  if (fileVideoArr.includes(extension)) {
    return (
      <div className="text-center">
        <img src='/media/tfi/files/video.webp' alt={extension} width={GRID_ICON_SIZE} />
        <div className="mt-2 fw-bold">{intl.formatMessage(R_.fileVideo)}</div>
      </div>
    )
  }

  if (filePdfArr.includes(extension)) {
    return (
      <div className="text-center">
        <img src='/media/tfi/files/pdf.png' alt={extension} width={GRID_ICON_SIZE} />
        <div className="mt-2 fw-bold">{intl.formatMessage(R_.filePdf)}</div>
      </div>
    )
  }

  if (fileAudioArr.includes(extension)) {
    return (
      <div className="text-center">
        <img src='/media/tfi/files/audio.webp' alt={extension} width={GRID_ICON_SIZE} />
        <div className="mt-2 fw-bold">{intl.formatMessage(R_.fileAudio)}</div>
      </div>
    )
  }

  if (fileTextArr.includes(extension)) {
    return (
      <div className="text-center">
        <img src='/media/tfi/files/txt.png' alt={extension} width={GRID_ICON_SIZE} />
        <div className="mt-2 fw-bold">{intl.formatMessage(R_.fileText)}</div>
      </div>
    )
  }

  if (fileCompressedArr.includes(extension)) {
    return (
      <div className="text-center">
        <img src='/media/tfi/files/rar.webp' alt={extension} width={GRID_ICON_SIZE} />
        <div className="mt-2 fw-bold">{intl.formatMessage(R_.fileCompressed)}</div>
      </div>
    )
  }

  // Unknown → generic icon
  return (
    <div className="text-center">
      <img src='/media/tfi/files/txt.png' alt={extension} width={GRID_ICON_SIZE} />
      <div className="mt-2 fw-bold">{intl.formatMessage(R_.fileDocument)}</div>
    </div>
  )
}

// Component for table/list view (small thumbnails)
export const RenderFilePreview = ({file}: {file: string}) => {
  const intl = useIntl()
  // Normalize and robustly extract extension:
  // - lowercases
  // - strips query/hash params
  // - takes the LAST segment after the dot (supports filenames with multiple dots)
  const cleaned = (file || '').toLowerCase()
  const noQuery = cleaned.split('?')[0].split('#')[0]
  const filename = noQuery.split('/').pop() || ''
  const extension = filename.includes('.') ? (filename.split('.').pop() as string) : ''

  // If we still couldn't detect an extension, show a generic icon
  if (!extension) {
    return <img src='/media/tfi/files/txt.png' alt={intl.formatMessage(R_.fileIconAlt)} width={WIDTH_ICON} />
  }

  // Word
  const fileWordArr = ['doc', 'docx', 'docm', 'dot', 'dotx', 'dotm', 'rtf']

  // Excel
  const fileExcelArr = ['xls', 'xlsx', 'xlsm', 'xlsb', 'xlt', 'xltx', 'xltm', 'csv']

  // PowerPoint
  const filePptArr = ['ppt', 'pptx', 'pptm', 'pot', 'potx', 'potm', 'pps', 'ppsx', 'ppsm']

  // Images (extend list; support common iOS/HEIF and TIFF short form)
  const fileImageArr = [
    'jpg',
    'jpeg',
    'jpe',
    'jfif',
    'png',
    'gif',
    'bmp',
    'tif',
    'tiff',
    'svg',
    'webp',
    'ico',
    'heic',
    'heif',
  ]

  // Videos
  const fileVideoArr = ['mp4', 'mov', 'avi', 'wmv', 'flv', 'mkv', 'webm', 'mpeg', '3gp', 'ogg']

  // PDF
  const filePdfArr = ['pdf']

  // Audio
  const fileAudioArr = ['mp3', 'wav', 'aac', 'flac', 'ogg', 'wma', 'm4a', 'aiff', 'alac']

  // Text-ish
  const fileTextArr = ['txt', 'md', 'log', 'json', 'xml', 'csv']

  // Compressed
  const fileCompressedArr = ['zip', 'rar', '7z', 'tar', 'gz']

  if (fileWordArr.includes(extension))
    return <img src='/media/tfi/files/word.webp' alt={extension} width={WIDTH_ICON} />
  if (fileExcelArr.includes(extension))
    return <img src='/media/tfi/files/excel.webp' alt={extension} width={WIDTH_ICON} />
  if (filePptArr.includes(extension))
    return <img src='/media/tfi/files/powerpoint.webp' alt={extension} width={WIDTH_ICON} />

  // For images, show a real thumbnail (works for .jpeg/.jpg even with querystrings and uppercase)
  if (fileImageArr.includes(extension)) {
    return (
      <img
        src={file}
        alt={extension}
        width={THUMB_SIZE}
        height={THUMB_SIZE}
        style={{objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd'}}
        onError={(e) => {
          // Fallback to generic icon if image cannot be loaded
          const target = e.currentTarget as HTMLImageElement
          target.onerror = null
          target.src = '/media/tfi/files/txt.png'
          target.width = WIDTH_ICON
          target.height = undefined as any
          target.style.objectFit = ''
          target.style.borderRadius = ''
          target.style.border = ''
        }}
      />
    )
  }

  if (fileVideoArr.includes(extension))
    return <img src='/media/tfi/files/video.webp' alt={extension} width={WIDTH_ICON} />
  if (filePdfArr.includes(extension))
    return <img src='/media/tfi/files/pdf.png' alt={extension} width={WIDTH_ICON} />
  if (fileAudioArr.includes(extension))
    return <img src='/media/tfi/files/audio.webp' alt={extension} width={WIDTH_ICON} />
  if (fileTextArr.includes(extension))
    return <img src='/media/tfi/files/txt.png' alt={extension} width={WIDTH_ICON} />
  if (fileCompressedArr.includes(extension))
    return <img src='/media/tfi/files/rar.webp' alt={extension} width={WIDTH_ICON} />

  // Unknown → generic icon
  return <img src='/media/tfi/files/txt.png' alt={extension} width={WIDTH_ICON} />
}
